import React, { useMemo } from 'react';

import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import {
  useInclusionDraftsContext,
  usePackageOptionsContext,
  usePropertySetupContext,
  useScrapeSessionContext,
  useSurchargeGridsContext,
} from '../../PricingToolSessionContext';
import { PRICING_OPTION_LABELS, audMoney } from '../../constants';
import { sumInclusionValue } from '../inclusions/inclusionsModel';
import { computeDailyMarketStats } from '../scrape/otaParity/computeOtaMarketStats';
import { computePrvThresholdColor } from '../scrape/otaParity/scrapePrvHeatColor';
import { computeStaySurchargeByDate } from '../surcharges/computeStaySurcharge';

import { computeOptionDiscountStats, computeOptionPricingSummary, leadSellAndNet } from './pricingSummaryModel';

function fmtMoney(value: number | null): string {
  return value != null ? audMoney(value) : '';
}

function fmtPct(value: number | null): string {
  return value != null ? `${value.toFixed(1)}%` : '';
}

export default function PricingSummaryTab() {
  const { stayInputs, parsedCsv } = useScrapeSessionContext();
  const { optionStates } = usePackageOptionsContext();
  const { drafts } = useInclusionDraftsContext();
  const { session: surchargeSession } = useSurchargeGridsContext();
  const {
    fx: { currency: netCurrency, audToSourceCurrencyRate },
  } = usePropertySetupContext();

  const marketStats = useMemo(
    () => (parsedCsv ? computeDailyMarketStats(parsedCsv, undefined, stayInputs?.blackoutDates) : []),
    [parsedCsv, stayInputs?.blackoutDates],
  );
  const surchargeByDate = useMemo(
    () =>
      stayInputs
        ? computeStaySurchargeByDate(
            marketStats.map((stats) => stats.date),
            stayInputs.nights,
            surchargeSession.grids,
          )
        : {},
    [marketStats, stayInputs, surchargeSession.grids],
  );

  const summaries = useMemo(
    () =>
      PRICING_OPTION_LABELS.map((_, optionIndex) => {
        const option = optionStates[optionIndex];
        const lead = stayInputs ? leadSellAndNet(option, stayInputs.nights) : null;
        const prvInclusionsAud = sumInclusionValue(drafts.filter((draft) => draft.pricingOptionIndex === optionIndex));
        const discountStats = computeOptionDiscountStats(
          marketStats,
          stayInputs,
          lead?.sell ?? null,
          prvInclusionsAud,
          surchargeByDate,
        );
        return computeOptionPricingSummary({
          optionIndex,
          option,
          stay: stayInputs,
          parsedCsv: parsedCsv ?? null,
          marketStats,
          discountStats,
          netCurrency,
          audToSourceCurrencyRate,
        });
      }),
    [optionStates, stayInputs, drafts, marketStats, surchargeByDate, parsedCsv, netCurrency, audToSourceCurrencyRate],
  );

  return (
    <TabPanelShell
      subtitle="Compare the three options side by side — sell price, margin, PRV and the month-by-month chart."
      title="Pricing Summary"
    >
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="caption">
        PRV = (inclusions value + daily market min − sell price) ÷ (inclusions value + daily market min) × 100,
        summarised as the mean across scraped dates.
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
        {summaries.map((summary) => (
          <Paper key={summary.optionIndex} sx={{ p: 2 }} variant="outlined">
            <Typography fontWeight={600} sx={{ mb: 1 }} variant="subtitle1">
              {PRICING_OPTION_LABELS[summary.optionIndex]}
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2">Sell: {fmtMoney(summary.sell)}</Typography>
              <Typography variant="body2">Margin: {fmtPct(summary.marginPct)}</Typography>
              <Typography
                sx={summary.prvPct != null ? computePrvThresholdColor(summary.prvPct) : undefined}
                variant="body2"
              >
                PRV: {fmtPct(summary.prvPct)}
              </Typography>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">PRV %</TableCell>
                  <TableCell align="right">Avg OTA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.monthRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} />
                  </TableRow>
                ) : (
                  summary.monthRows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell
                        align="right"
                        sx={row.medianPrvPct != null ? computePrvThresholdColor(row.medianPrvPct) : undefined}
                      >
                        {fmtPct(row.medianPrvPct)}
                      </TableCell>
                      <TableCell align="right">{fmtMoney(row.avgOta)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Box>
    </TabPanelShell>
  );
}
